import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../ui/carousel";
import { DoctorCard } from "../doctor-card";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

export default function OurDoctorsWidget() {
  const [doctors, setDoctors] = useState([]);

  const [content, setContent] = useState({
    badge: "",
    heading: "",
    description: "",
    buttonText: "",
    buttonLink: "",
  });

  useEffect(() => {
    fetch("/wp-json/custom/v1/homepage")
      .then((res) => res.json())
      .then((data) => {

        const widget = data?.our_doctors || {};

        // ✅ SET CMS CONTENT
        setContent({
          badge: widget?.our_doctors_badge || "",
          heading: widget?.our_doctors_heading || "",
          description: widget?.our_doctors_description || "",
          buttonText: widget?.our_doctors_button_text || "",
          buttonLink: widget?.our_doctors_button_link || "/doctors",
        });

        // ✅ GET IDS FROM homepage_doctor_orders
        const ids = widget?.homepage_doctor_orders
          ?.split(",")
          .map((id) => id.trim())
          .filter(Boolean);

        if (ids && ids.length > 0) {
          fetchDoctors(ids);
        }
      })
      .catch((err) => console.error("CMS ERROR:", err));
  }, []);

  const fetchDoctors = (ids) => {
    const url = `/wp-json/wp/v2/doctors?include=${ids.join(
      ","
    )}&_embed&per_page=100`;

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        // ✅ MAINTAIN ORDER FROM CMS
        const sortedDoctors = ids
          .map((id) =>
            data.find((doc) => String(doc.id) === String(id))
          )
          .filter(Boolean);

        setDoctors(sortedDoctors);
      })
      .catch((err) => {
        console.error("DOCTORS ERROR:", err);
        setDoctors([]);
      });
  };

  return (
    <section className="py-12 bg-gray-100">
      <div className="container px-4 mx-auto">

        {/* HEADER */}
        <div className="flex flex-col items-center mb-10 text-center">
          {content.badge && (
            <Badge className="mb-2">{content.badge}</Badge>
          )}

          {content.heading && (
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">
              {content.heading}
            </h2>
          )}

          {content.description && (
            <p className="max-w-2xl text-[#485260]">
              {content.description}
            </p>
          )}
        </div>

        {/* CAROUSEL */}
        <Carousel className="w-full max-w-5xl mx-auto">
          <CarouselContent>
            {doctors.length > 0 ? (
              doctors.map((doctor) => {
                const name = doctor?.title?.rendered || "";
                const specialty =
                  doctor?.acf?.designation || "";
                const experience =
                  doctor?.acf?.latest_achievement || "";
                const image =
                  (doctor?._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "/placeholder.svg").replace(/\.(png|jpe?g|gif|bmp|tiff?)$/i, ".webp");

                return (
                  <CarouselItem
                    key={doctor.id}
                    className="md:basis-1/2 lg:basis-1/3"
                  >
                    <DoctorCard
                     name={name}
                      specialty={specialty}
                      experience={experience}
                      image={image}
                      href={`/doctors/${doctor.slug}`}
                      hospitalDoctorId={doctor?.acf?.doctor_id || doctor.id}
                    />
                  </CarouselItem>
                );
              })
            ) : (
              <p className="text-center w-full py-6">
                Loading doctors...
              </p>
            )}
          </CarouselContent>

          {/* ARROWS */}
          <div className="flex justify-center mt-8">
            <CarouselPrevious className="relative left-0 mr-2" />
            <CarouselNext className="relative right-0 ml-2" />
          </div>
        </Carousel>

        {/* BUTTON */}
        {content.buttonText && (
          <div className="flex justify-center mt-8">
            <Button
              variant="outline"
              className="gap-2 border-primary text-primary hover:bg-primary hover:text-white"
              asChild
            >
              <Link to={content.buttonLink}>
                {content.buttonText}
                <ChevronRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}